import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Paginado } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { Tag } from '../components/Tag';
import { PapeisEPermissoes, type Papel } from '../components/PapeisEPermissoes';
import {
  Campo,
  CampoBusca,
  Carregando,
  ErroBox,
  Form,
  LinhaCampos,
  Modal,
  ModalRodape,
  Secao,
  Paginacao,
  Vazio,
} from '../components/ui';
import { gerarSenha, mensagemDeAcesso } from '../dados/mensagem-acesso';

interface Usuario {
  id: string;
  name: string;
  email: string;
  status: string;
  lastLoginAt: string | null;
  roles: { role: { id: string; name: string; slug: string } }[];
  eventAccess: { event: { id: string; name: string } }[];
}

interface EventoResumo {
  id: string;
  name: string;
}

interface RespostaConvite {
  user: Usuario;
  /** Nulos quando a senha foi definida na criação. */
  inviteToken: string | null;
  inviteExpiresAt: string | null;
}

/**
 * A senha nunca volta do servidor — ele guarda só o hash. Ela vem do campo que
 * acabou de ser preenchido, vive nesta tela e some quando a janela fecha.
 */
type Criado = RespostaConvite & { senha?: string };

const CORES_STATUS: Record<string, string> = {
  ATIVO: 'success',
  CONVIDADO: 'info',
  BLOQUEADO: 'danger',
  INATIVO: 'muted',
};

export function ConfigUsuarios() {
  const { usuario: eu } = useAuth();
  const cliente = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [convidando, setConvidando] = useState(false);
  const [vendoPapeis, setVendoPapeis] = useState(false);
  const [convite, setConvite] = useState<Criado | null>(null);

  // Controlada porque a confirmação compara enquanto se digita — descobrir a
  // divergência só no envio é o pior momento para descobrir.
  const [senha, setSenha] = useState('');
  const [senhaConfirma, setSenhaConfirma] = useState('');

  const erroSenha =
    senhaConfirma && senha !== senhaConfirma
      ? 'As senhas não conferem.'
      : senha && senha.length < 10
        ? 'Use ao menos 10 caracteres.'
        : undefined;

  /** Zera o formulário ao fechar, para o próximo convite não herdar a senha. */
  function fecharConvite() {
    setConvidando(false);
    setSenha('');
    setSenhaConfirma('');
  }
  const [editando, setEditando] = useState<Usuario | null>(null);
  const [eventosSelecionados, setEventosSelecionados] = useState<string[]>([]);

  const params = new URLSearchParams({ page: String(pagina), perPage: '20' });
  if (busca) params.set('search', busca);

  const consulta = useQuery({
    queryKey: ['usuarios', pagina, busca],
    queryFn: () => api<Paginado<Usuario>>(`/users?${params}`),
  });

  const papeis = useQuery({ queryKey: ['papeis'], queryFn: () => api<Papel[]>('/roles') });

  const eventos = useQuery({
    queryKey: ['eventos-para-acesso'],
    queryFn: () =>
      api<{ data: EventoResumo[] }>('/events?perPage=100').then((r) => r.data),
  });

  const convidar = useMutation({
    mutationFn: (dados: Record<string, string>) =>
      api<RespostaConvite>('/users', {
        method: 'POST',
        body: {
          name: dados.name,
          email: dados.email,
          roleIds: dados.roleId ? [dados.roleId] : [],
          // Só vai quando preenchida: campo vazio significa "manda convite", e
          // uma string vazia seria recusada pelo `min(10)` do servidor.
          ...(senha ? { password: senha } : {}),
        },
      }),
    onSuccess: (resposta) => {
      cliente.invalidateQueries({ queryKey: ['usuarios'] });
      // Lida ANTES de `fecharConvite` zerar o formulário — depois dela a senha
      // não existe em lugar nenhum, nem aqui nem no servidor.
      setConvite({ ...resposta, senha: senha || undefined });
      fecharConvite();
    },
  });

  const atualizar = useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: Record<string, unknown> }) =>
      api(`/users/${id}`, { method: 'PATCH', body: corpo }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['usuarios'] });
      setEditando(null);
    },
  });

  return (
    <Secao
        titulo="Usuários"
        descricao="Quem acessa este painel. Participantes não têm conta — falam pelo WhatsApp."
        acao={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={() => setVendoPapeis(true)}>
              <i className="ti ti-shield-lock" /> Papéis e permissões
            </button>
            <button className="btn btn-primary" onClick={() => setConvidando(true)}>
              <i className="ti ti-user-plus" /> Convidar
            </button>
          </div>
        }
      >

      <div className="barra-filtros">
        <CampoBusca
          valor={busca}
          aoMudar={(v) => {
            setBusca(v);
            setPagina(1);
          }}
          placeholder="Buscar por nome ou e-mail…"
        />
      </div>

      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}

      {consulta.data &&
        (consulta.data.data.length === 0 ? (
          <Vazio icone="ti-users" titulo="Nenhum usuário encontrado" />
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="tabela-envolvente">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>Papéis</th>
                    <th>Eventos</th>
                    <th>Último acesso</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {consulta.data.data.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <div className="celula-principal">
                          {u.name}
                          {u.id === eu?.id && (
                            <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}> (você)</span>
                          )}
                        </div>
                        <div className="celula-secundaria">{u.email}</div>
                      </td>
                      <td>
                        {u.roles.length === 0 ? (
                          <span style={{ color: 'var(--text-dim)' }}>Sem papel</span>
                        ) : (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {u.roles.map((r) => (
                              <Tag key={r.role.id}>{r.role.name}</Tag>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        {u.eventAccess.length === 0 ? (
                          <span style={{ color: 'var(--text-dim)' }}>Todos</span>
                        ) : (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {u.eventAccess.map((a) => (
                              <Tag key={a.event.id} type="info">
                                {a.event.name}
                              </Tag>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        {u.lastLoginAt ? (
                          new Date(u.lastLoginAt).toLocaleString('pt-BR')
                        ) : (
                          <span style={{ color: 'var(--text-dim)' }}>Nunca entrou</span>
                        )}
                      </td>
                      <td>
                        <Tag type={CORES_STATUS[u.status] ?? 'muted'}>{u.status}</Tag>
                      </td>
                      <td>
                        <div className="acoes-linha">
                          {/* A própria conta não aparece com ações: o servidor
                              recusa auto-bloqueio e troca dos próprios papéis. */}
                          {u.id !== eu?.id && (
                            <button
                              className="icon-btn"
                              title="Editar"
                              onClick={() => {
                                setEventosSelecionados(u.eventAccess.map((a) => a.event.id));
                                setEditando(u);
                              }}
                            >
                              <i className="ti ti-edit" style={{ fontSize: 15 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginacao meta={consulta.data.meta} aoMudar={setPagina} />
          </div>
        ))}

      {convidando && (
        <Modal
          titulo="Novo usuário"
          descricao="Defina uma senha para a pessoa já entrar, ou deixe em branco para ela criar a própria pelo link do convite."
          aoFechar={fecharConvite}
          largura={560}
        >
          <Form aoEnviar={(dados) => convidar.mutate(dados)}>
            {convidar.isError && <div className="form-erro">{(convidar.error as Error).message}</div>}

            <Campo label="Nome" name="name" obrigatorio autoFocus />
            <Campo label="E-mail" name="email" tipo="email" obrigatorio />
            <Campo
              label="Papel"
              name="roleId"
              opcoes={[
                { valor: '', rotulo: 'Sem papel (não verá nada)' },
                ...(papeis.data ?? []).map((p) => ({ valor: p.id, rotulo: p.name })),
              ]}
              ajuda="Define o que a pessoa pode fazer. Pode ser alterado depois."
            />

            {/* O atalho fica aqui porque é aqui que a dúvida aparece: na hora
                de escolher, não antes. */}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setVendoPapeis(true)}
            >
              <i className="ti ti-shield-lock" /> Ver o que cada papel pode fazer
            </button>

            <div className="divider" style={{ margin: '18px 0' }} />

            <LinhaCampos>
              <Campo
                label="Senha"
                name="senha"
                tipo="password"
                valor={senha}
                aoMudar={setSenha}
                ajuda="Em branco = a pessoa define a própria senha pelo link."
              />
              <Campo
                label="Confirmar senha"
                name="senhaConfirma"
                tipo="password"
                valor={senhaConfirma}
                aoMudar={setSenhaConfirma}
                erro={erroSenha}
              />
            </LinhaCampos>

            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                const gerada = gerarSenha();
                setSenha(gerada);
                setSenhaConfirma(gerada);
              }}
            >
              <i className="ti ti-wand" /> Gerar senha
            </button>

            {senha && (
              <div className="aviso" style={{ marginTop: 16 }}>
                <i className="ti ti-info-circle" />
                <span>
                  A conta nasce ativa e <strong>você</strong> conhece a senha. Peça a troca no
                  primeiro acesso.
                </span>
              </div>
            )}

            <ModalRodape>
              <button type="button" className="btn btn-secondary" onClick={fecharConvite}>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={convidar.isPending || !!erroSenha}
              >
                {convidar.isPending ? 'Criando…' : senha ? 'Criar usuário' : 'Criar convite'}
              </button>
            </ModalRodape>
          </Form>
        </Modal>
      )}

      {convite && (
        <ModalCriado
          criado={convite}
          cliente={eu?.organization?.tradeName ?? 'a plataforma'}
          aoFechar={() => setConvite(null)}
        />
      )}

      {editando && (
        <Modal titulo={`Editar ${editando.name}`} aoFechar={() => setEditando(null)}>
          <Form
            aoEnviar={(dados) =>
              atualizar.mutate({
                id: editando.id,
                corpo: {
                  name: dados.name,
                  status: dados.status,
                  roleIds: dados.roleId ? [dados.roleId] : [],
                  eventIds: eventosSelecionados,
                },
              })
            }
          >
            {atualizar.isError && (
              <div className="form-erro">{(atualizar.error as Error).message}</div>
            )}

            <Campo label="Nome" name="name" obrigatorio valorInicial={editando.name} />
            <Campo
              label="Papel"
              name="roleId"
              valorInicial={editando.roles[0]?.role.id ?? ''}
              opcoes={[
                { valor: '', rotulo: 'Sem papel' },
                ...(papeis.data ?? []).map((p) => ({ valor: p.id, rotulo: p.name })),
              ]}
              ajuda="Trocar o papel desconecta a pessoa. Ela entra de novo já com o acesso novo."
            />
            <Campo
              label="Status"
              name="status"
              valorInicial={editando.status === 'CONVIDADO' ? 'ATIVO' : editando.status}
              opcoes={[
                { valor: 'ATIVO', rotulo: 'Ativo' },
                { valor: 'BLOQUEADO', rotulo: 'Bloqueado' },
                { valor: 'INATIVO', rotulo: 'Inativo' },
              ]}
              ajuda="Bloquear encerra as sessões da pessoa imediatamente."
            />

            <SeletorEventos
              eventos={eventos.data ?? []}
              selecionados={editando.eventAccess.map((a) => a.event.id)}
              aoMudar={setEventosSelecionados}
            />

            <ModalRodape>
              <button type="button" className="btn btn-secondary" onClick={() => setEditando(null)}>
                Cancelar
              </button>
              <button className="btn btn-primary" type="submit" disabled={atualizar.isPending}>
                {atualizar.isPending ? 'Salvando…' : 'Salvar'}
              </button>
            </ModalRodape>
          </Form>
        </Modal>
      )}

      {vendoPapeis && <PapeisEPermissoes aoFechar={() => setVendoPapeis(false)} />}
    </Secao>
  );
}

/**
 * O resultado da criação, nos dois caminhos.
 *
 * Uma janela só, e não uma para cada: o que muda entre "definiu senha" e
 * "mandou convite" é o miolo da mensagem — o resto (copiar agora, some ao
 * fechar, o servidor só guarda o hash) vale igual para os dois.
 */
function ModalCriado({
  criado,
  cliente,
  aoFechar,
}: {
  criado: Criado;
  cliente: string;
  aoFechar: () => void;
}) {
  const [copiado, setCopiado] = useState(false);

  const mensagem = mensagemDeAcesso({
    cliente,
    email: criado.user.email,
    senha: criado.senha,
    inviteToken: criado.inviteToken,
  });

  return (
    <Modal
      titulo={criado.senha ? 'Usuário criado' : 'Convite criado'}
      descricao={
        criado.senha
          ? `${criado.user.name} já pode entrar com a senha que você definiu.`
          : `Envie a mensagem abaixo para ${criado.user.name} definir a senha.`
      }
      aoFechar={aoFechar}
      largura={560}
    >
      <div className="aviso" style={{ marginBottom: 16 }}>
        <i className="ti ti-alert-triangle" />
        <span>
          Copie agora: o servidor guarda apenas o hash, então isto não pode ser recuperado
          depois que você fechar.{' '}
          {criado.inviteExpiresAt &&
            `O link vale até ${new Date(criado.inviteExpiresAt).toLocaleDateString('pt-BR')}.`}
        </span>
      </div>

      <label className="campo">
        <span className="campo-label">Mensagem para {criado.user.name}</span>
        <textarea
          className="form-input mono"
          readOnly
          rows={criado.senha ? 8 : 7}
          value={mensagem}
          style={{ resize: 'vertical', lineHeight: 1.6 }}
          onFocus={(e) => e.target.select()}
        />
      </label>

      <ModalRodape>
        <button
          className="btn btn-secondary"
          onClick={() => {
            void navigator.clipboard.writeText(mensagem);
            setCopiado(true);
          }}
        >
          <i className={`ti ${copiado ? 'ti-check' : 'ti-copy'}`} />
          {copiado ? 'Copiado' : 'Copiar mensagem'}
        </button>
        <button className="btn btn-primary" onClick={aoFechar}>
          Fechar
        </button>
      </ModalRodape>
    </Modal>
  );
}

/**
 * Restrição por evento.
 *
 * Nada marcado = sem restrição, que é o padrão e o caso da maioria. O rótulo
 * diz isso explicitamente porque "nenhum marcado" poderia ser lido como
 * "nenhum evento" — o oposto do que acontece.
 */
function SeletorEventos({
  eventos,
  selecionados,
  aoMudar,
}: {
  eventos: EventoResumo[];
  selecionados: string[];
  aoMudar: (ids: string[]) => void;
}) {
  if (eventos.length === 0) return null;

  function alternar(id: string) {
    aoMudar(
      selecionados.includes(id) ? selecionados.filter((e) => e !== id) : [...selecionados, id],
    );
  }

  return (
    <div className="campo">
      <span className="campo-label">Restringir a eventos</span>

      <div className="lista-selecao">
        {eventos.map((e) => (
          <label key={e.id} className="item-selecao">
            <input
              type="checkbox"
              checked={selecionados.includes(e.id)}
              onChange={() => alternar(e.id)}
            />
            {e.name}
          </label>
        ))}
      </div>

      <span className="campo-ajuda">
        {selecionados.length === 0
          ? 'Nenhum marcado: a pessoa vê TODOS os eventos da organização.'
          : `Verá apenas ${selecionados.length} evento(s). Passa a valer no próximo refresh do token (até 15 min).`}
      </span>
    </div>
  );
}
